import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { fetchUsersCheckInBlockers } from "src/models/user/user";
import { CheckInBlockerDialogProps } from "src/shared/CheckinBlocker/CheckInBlockerDialog";
import { CheckinBlockerUser } from "src/shared/CheckinBlocker/_types";
import { Identifier } from "src/types/utilityTypes";

type UseCheckInBlockerOptions = Partial<
  Pick<CheckInBlockerDialogProps, 'title' | 'subtitle' | 'actions' | 'inlineActions'> & {
    showUserDetails: boolean | ((users: CheckinBlockerUser[]) => boolean);
  }
>;

export interface CheckBlockersInput {
  userIds?: Identifier[];
  barcodes?: string[];
}

export type UseCheckInBlockerReturn = [
  CheckInBlockerDialogProps,
  (input: CheckBlockersInput) => Promise<CheckinBlockerUser[]>,
  (users: CheckinBlockerUser[]) => void,
  boolean,
];

export function useCheckInBlocker(
  initialOptions: UseCheckInBlockerOptions = {}
): UseCheckInBlockerReturn {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<CheckinBlockerUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: CheckinBlockerUser[]) => void) | null
  >(null);

  const openWithUsers = (newUsers: CheckinBlockerUser[]) => {
    setUsers(newUsers);
    setOpen(true);
  };

  const checkBlockers = useCallback(async ({ userIds = [], barcodes = [] }: CheckBlockersInput): Promise<CheckinBlockerUser[]> => {
    if (userIds.length === 0 && barcodes.length === 0) return [];

    setIsLoading(true);
    const result = await queryClient.fetchQuery<CheckinBlockerUser[]>({
      queryKey: ['checkinBlockers', userIds, barcodes],
      queryFn: () => fetchUsersCheckInBlockers({ userIds, barcodes }),
    });
    setIsLoading(false);

    if (result.length === 0) return [];

    setUsers(result);
    setOpen(true);

    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, [queryClient, setIsLoading, setUsers, setOpen, setResolvePromise]);

  const handleClose = () => {
    setOpen(false);
    if (resolvePromise) {
      resolvePromise(users);
      setResolvePromise(null);
    }
  };

  const resolvedShowUserDetails =
    typeof initialOptions.showUserDetails === 'function'
      ? initialOptions.showUserDetails(users)
      : initialOptions.showUserDetails;

  const dialogProps: CheckInBlockerDialogProps = {
    open,
    onClose: handleClose,
    title: initialOptions.title,
    subtitle: initialOptions.subtitle,
    actions: initialOptions.actions,
    inlineActions: initialOptions.inlineActions,
    showUserDetails: resolvedShowUserDetails,
    users,
  };

  return [dialogProps, checkBlockers, openWithUsers, isLoading];
}
